/**
 * Which figure in a comparison row is the stronger one, stated as a fact.
 *
 * Design screen 13c marks one figure per row in green and captions it "Green
 * marks the better number." Ours drew every cell in one ink, and that was the
 * last visible difference in a comparison table.
 *
 * "BETTER" IS A JUDGEMENT AND "LOWER" IS NOT, which is the whole of why this
 * can exist in an engine that describes and never prescribes. A P/E is price
 * divided by earnings, so the lower one is literally fewer rupees paid per
 * rupee earned — arithmetic, not a view about what anyone should buy. P/B runs
 * the same way; EPS, ROE and a dividend run the other, by the same
 * definitional logic. The mark states that direction, `strongerLegend` says it
 * in words, and neither says anything about what to do with it.
 *
 * A MEASURE WITH NO ENTRY IS NEVER MARKED, and price is the case that matters:
 * a cheaper share is not a better one, and nothing here knows which of two
 * prices a reader should prefer. Volume and market cap are the same. Silence
 * is the honest answer and it is also what stops the table implying a ranking
 * it cannot support.
 */
import { cellNumber, humanLabel, type CellValue } from '@/features/mitra/lib/figures'

/** `-1` where the lower figure is the stronger one, `1` where the higher is. */
const DIRECTION: Record<string, -1 | 1> = {
  // Fewer rupees paid per rupee of earnings, and per rupee of book.
  pe: -1,
  pb: -1,
  // More earned, more returned on equity, more paid out.
  eps: 1,
  roe: 1,
  dividend_yield_pct: 1,
  cash_pct: 1,
  bonus_pct: 1,
  // A bad-loan ratio: less of it is less of it.
  npl_ratio: -1,
}

const key = (measure: string) => measure.toLowerCase().replace(/ /g, '_')

/**
 * The index of the stronger figure, or null when the row has no stronger one.
 *
 * Null covers every case where marking would assert something: a measure with
 * no definitional direction, a tie, a missing figure, and a row with fewer
 * than two figures to compare.
 */
export function strongerIndex(
  measure: string,
  values: readonly (CellValue | null)[],
): number | null {
  const direction = DIRECTION[key(measure)]
  if (direction === undefined || values.length < 2) return null
  const numbers = values.map((v) => (v === null ? null : cellNumber(v)))
  if (numbers.some((n) => n === null || !Number.isFinite(n))) return null
  const scored = numbers as number[]
  const best = direction === 1 ? Math.max(...scored) : Math.min(...scored)
  // A tie has no stronger figure, and colouring the first would assert an
  // order the row does not have.
  if (scored.filter((n) => n === best).length > 1) return null
  return scored.indexOf(best)
}

/**
 * What the green mark means, in the direction each measure actually runs.
 *
 * THE MARK WITHOUT THIS IS A VERDICT. Two marks on this surface have already
 * shipped meaning something and saying nothing — the pin on an allocation bar
 * and the dagger on a derived figure — and both were reported as confusing by
 * the person reading them. A colour that silently means "better" would be the
 * same mistake with more at stake.
 *
 * Null when nothing in the table is markable, so no legend is drawn for a mark
 * that is not there.
 */
export function strongerLegend(measures: readonly string[]): string | null {
  const named: string[] = []
  const seen = new Set<string>()
  for (const measure of measures) {
    const direction = DIRECTION[key(measure)]
    if (direction === undefined) continue
    const word = direction === 1 ? 'higher' : 'lower'
    const phrase = `the ${word} ${humanLabel(measure)}`
    if (seen.has(phrase)) continue
    seen.add(phrase)
    named.push(phrase)
  }
  if (named.length === 0) return null
  const last = named[named.length - 1] ?? ''
  const list = named.length === 1 ? last : `${named.slice(0, -1).join(', ')} and ${last}`
  return `Green marks ${list}.`
}
